import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { WizardComponent, WizardStepComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildDisplayGroupedSourceItems,
  DisplayGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getDisplayGroupedPartLabels,
  getMonitoringOtherProductsLabel,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { OptionDisplay } from "@project/monitoring/option-display.service";
import { of, Subject, switchMap } from "rxjs";

export enum DisplaySelectorType {
  DISPLAY = "DISPLAY",
  ACCESSORIES = "ACCESSORIES",
  OVERVIEW = "OVERVIEW",
}

@Component({
  selector: "app-display-configuration",
  imports: [
    ConfigurationWizardBackButtonComponent,
    WizardComponent,
    WizardStepComponent,
    ProductConfigStepperBarComponent,
    OptionSelectorComponent,
    MonitoringOverviewComponent,
  ],
  templateUrl: "./display-configuration.component.html",
  styleUrl: "./display-configuration.component.scss",
})
export class DisplayConfigurationComponent implements OnInit {
  protected readonly DisplaySelectorTitleKeyMap: Record<DisplaySelectorType, string> = {
    [DisplaySelectorType.DISPLAY]: $localize`:@@displayConfiguration.step1.title:Select display`,
    [DisplaySelectorType.ACCESSORIES]: $localize`:@@displayConfiguration.step2.title:Select accessories`,
    [DisplaySelectorType.OVERVIEW]: $localize`:@@displayConfiguration.step3.title:Display configuration summary`,
  };

  protected readonly overviewCategoryLabels: DisplayGroupedPartLabels = getDisplayGroupedPartLabels();
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  protected project!: Project;
  protected originalConfiguration?: DisplayConfiguration;
  protected activeStep = 0;
  protected steps = 2;
  protected accessoriesAvailable = false;

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly DisplaySelectorType = DisplaySelectorType;
  protected readonly MonitoringCategory = MonitoringCategory;
  protected groupedSections: MonitoringGroupedPartSection[] = [];

  private activeRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected displayFamily?: Family;
  protected displayStep?: FamilyStep;
  protected family?: Family;
  protected accessoryOptions: FamilyStepOption[] = [];
  protected selectedDisplay?: OptionDisplay;
  protected selectedAccessories?: OptionDisplayWithQuantity[];
  protected selectedDisplayForm!: FormGroup;

  constructor() {
    this.overviewItems$
      .pipe(
        switchMap((items) =>
          items.length
            ? this.monitoringGroupedPartsService.buildSections(items, MonitoringCategory.DISPLAYS, this.otherProductsLabel)
            : of([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.displays.find((d) => d.id === configId);
    }

    this.monitoringProductService
      .loadCategoryFamiliesFromDB(MonitoringCategory.DISPLAYS, this.productService.getFilterCountry())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((families) => {
        this.displayFamily = families[0];
        if (this.displayFamily) {
          this.displayStep = this.displayFamily.steps[0];
          this.family = this.displayFamily;
          this.accessoryOptions = (this.displayFamily.steps[1]?.options ?? []) as FamilyStepOption[];
          this.accessoriesAvailable = this.accessoryOptions.length > 0;
        }
      });
  }

  protected setSelectedDisplay(selections: OptionDisplayWithQuantity[]) {
    this.selectedDisplay = selections[0]?.optionDisplay;
  }

  protected setSelectedAccessories($event: OptionDisplayWithQuantity[]) {
    this.selectedAccessories = $event;
  }

  protected setActiveStep(event: number) {
    if (event === 1 && !this.accessoriesAvailable) {
      if (this.activeStep === 0) {
        this.configurationWizard.setActiveStepValid();
        this.configurationWizard.nextStep();
      } else {
        this.configurationWizard.previousStep();
      }
    } else {
      this.activeStep = event;
    }
  }

  protected onSubmit() {
    this.collectOverviewData();

    if (this.activeStep === 2) {
      this.persistConfiguration();
      return;
    }

    if (this.activeStep === 0) {
      this.selectedDisplayForm.markAllAsTouched();
      if (!this.selectedDisplayForm.valid) return;
    }

    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  private persistConfiguration() {
    if (this.originalConfiguration) {
      this.updateDisplayConfig(this.originalConfiguration);
    } else {
      this.createNewDisplayConfig();
    }
  }

  private createNewDisplayConfig() {
    if (!this.selectedDisplay || !this.family) return;

    const displayConfig = MonitoringConfigurationFactory.createDisplay(this.family.familyName, this.family, this.project, {
      display: MonitoringProductItem.create(this.selectedDisplay.option, 1),
      accessories: this.selectedAccessories?.map((acc) => MonitoringProductItem.create(acc.optionDisplay.option, acc.quantity)),
    });

    this.project.addDisplays(displayConfig);
    this.backButton.navigateBack();
  }

  private updateDisplayConfig(config: DisplayConfiguration) {
    if (!this.selectedDisplay || !this.family) return;

    config.update(
      this.family.familyName,
      this.family.familyNumber,
      MonitoringProductItem.create(this.selectedDisplay.option, 1),
      this.selectedAccessories?.map((acc) => MonitoringProductItem.create(acc.optionDisplay.option, acc.quantity)) ?? [],
    );
    this.backButton.navigateBack();
  }

  private collectOverviewData() {
    if (!this.selectedDisplay) return;
    const items: MonitoringGroupedPartSourceItem[] = buildDisplayGroupedSourceItems(
      {
        display: { option: this.selectedDisplay.option, quantity: 1 },
        accessories: this.selectedAccessories?.map((acc) => ({
          option: acc.optionDisplay.option,
          quantity: acc.quantity,
        })),
      },
      this.overviewCategoryLabels,
    );
    this.overviewItems$.next(items);
  }
}
